import { useState, useEffect, useRef, useCallback } from "react";

// Cache media query objects
const mediaQueryCache = new Map();

// Function to guess initial match state from query
function guessInitialState(query: string): boolean {
  // Default sizes commonly used in media queries
  if (query.includes("min-width")) {
    // For min-width queries, default to false (mobile first)
    // Extract the pixel value and check against common breakpoints
    const pixelMatch = query.match(/min-width:\s*(\d+)px/);
    if (pixelMatch) {
      const pixels = parseInt(pixelMatch[1], 10);
      // For large breakpoints (desktop), default to false
      return pixels < 768; // Default true only for mobile-focused min-width
    }
    return false;
  }

  if (query.includes("max-width")) {
    // For max-width queries, default to true (assuming mobile focused)
    const pixelMatch = query.match(/max-width:\s*(\d+)px/);
    if (pixelMatch) {
      const pixels = parseInt(pixelMatch[1], 10);
      // Default to true for mobile/tablet dimensions
      return pixels >= 768;
    }
    return true;
  }

  // For dark mode or other preference-based queries, default to false
  if (
    query.includes("prefers-color-scheme") ||
    query.includes("prefers-reduced-motion")
  ) {
    return false;
  }

  // Default fallback
  return false;
}

export function useMediaQuery(query: string): boolean {
  // Calculate a smart default based on the query to reduce layout shifts
  const initialState = guessInitialState(query);

  // Use state with the guessed initial value
  const [matches, setMatches] = useState(initialState);

  // Use refs to avoid unnecessary re-renders and cleanup issues
  const mqlRef = useRef<MediaQueryList | null>(null);
  const handlerRef = useRef<((e: MediaQueryListEvent) => void) | null>(null);

  // Memoize the handler to avoid recreation on re-renders
  const createHandler = useCallback(() => {
    const handler = (e: MediaQueryListEvent) => {
      setMatches(e.matches);
    };
    handlerRef.current = handler;
    return handler;
  }, []);

  useEffect(() => {
    // Only run on client
    if (typeof window === "undefined") return;

    // Cleanup previous listener if query changes
    return () => {
      if (mqlRef.current && handlerRef.current) {
        mqlRef.current.removeEventListener("change", handlerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Only run on client
    if (typeof window === "undefined") return;

    // Use cached media query object if available
    let mql = mediaQueryCache.get(query);
    if (!mql) {
      mql = window.matchMedia(query);
      mediaQueryCache.set(query, mql);
    }

    mqlRef.current = mql;

    // Set accurate value as soon as possible
    if (matches !== mql.matches) {
      setMatches(mql.matches);
    }

    // Setup event listener with memoized handler
    const handler = createHandler();
    mql.addEventListener("change", handler);

    // Clean up
    return () => {
      mql.removeEventListener("change", handler);
    };
  }, [query, matches, createHandler]);

  return matches;
}

// Export a higher-level convenience hook for common breakpoints
export function useBreakpoint() {
  const isMobile = useMediaQuery("(max-width: 639px)");
  const isTablet = useMediaQuery("(min-width: 640px) and (max-width: 1023px)");
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  return { isMobile, isTablet, isDesktop };
}
